// src/components/AuthGate.jsx
import React, { useEffect, useState } from 'react';
import App from './App';
import LandingPage from './landing/LandingPage';
import { readToken, clearSession } from './AccountPanel';
import { API_BASE } from '../utils/apiBase';

/**
 * Landing page or dashboard — decided by whether there is a LIVE session.
 *
 * A token in sessionStorage is only a claim. It can have expired or been revoked
 * on the server, and trusting it would open the dashboard onto an empty project
 * list for someone who is not actually signed in. So a stored token is checked
 * against /api/account/me before the dashboard renders, and until that answers
 * the screen says so rather than flashing the landing page and then swapping.
 *
 * After that, `decon:auth` is the single source of truth. AccountPanel announces
 * it on sign-in and sign-out, and App's 401 handling announces it when a session
 * dies mid-use — so signing out anywhere returns here, to the landing page.
 *
 * "Unreachable" is its own state. A token plus a server that is not answering
 * is neither signed in nor signed out, and showing the landing page would tell
 * a signed-in person they had been logged out when they had not.
 */
export default function AuthGate() {
  const [status, setStatus] = useState(() => (readToken() ? 'checking' : 'signedOut'));

  useEffect(() => {
    const onAuth = (e) => setStatus(e.detail?.signedIn ? 'signedIn' : 'signedOut');
    window.addEventListener('decon:auth', onAuth);
    return () => window.removeEventListener('decon:auth', onAuth);
  }, []);

  useEffect(() => {
    if (status !== 'checking') return undefined;
    let dead = false;
    // authFetch attaches the stored token to this request.
    fetch(`${API_BASE}/api/account/me`)
      .then((r) => {
        if (dead) return;
        if (r.ok) setStatus('signedIn');
        else if (r.status === 401) { clearSession(); setStatus('signedOut'); }
        else setStatus('unreachable');
      })
      .catch(() => { if (!dead) setStatus('unreachable'); });
    return () => { dead = true; };
  }, [status]);

  if (status === 'signedIn') return <App />;
  if (status === 'signedOut') return <LandingPage />;

  return (
    <div className="lp-gate" role="status" aria-live="polite">
      <div className="logo">VAUOM</div>
      {status === 'checking' ? (
        <p className="lp-gate-text">Checking your session…</p>
      ) : (
        <>
          <p className="lp-gate-text">
            Could not reach the VAUOM server, so your session cannot be confirmed.
            Check it is running, then try again.
          </p>
          <div className="lp-gate-actions">
            <button type="button" className="btn primary lp-btn-primary" onClick={() => setStatus('checking')}>
              Try again
            </button>
            <button type="button" className="btn" onClick={() => { clearSession(); setStatus('signedOut'); }}>
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
